import {IcnArrowDown, IcnArrowRightUp, IcnInfoFilled, IcnSwitchHorizontal} from "../../assets/icons";

import clsx from "clsx";
import * as Yup from 'yup'
import {Form, Formik} from "formik";
import Tippy from "@tippyjs/react";

import {DummyCurrencyOptions, HomeRepayCreditFormOnRampValues} from "../../utils";

import ModalFormWrapper from "../common/ModalFormWrapper";
import FormFieldset from "../form-elements/FormFieldset";
import FormInputNumber from "../form-elements/FormInputNumber";
import FormInputSelect from "../form-elements/FormInputSelect";
import FormGroupErrorText from "../form-elements/FormGroupErrorText";
import BlockNumber from "../common/BlockNumber";
import BlockTokenPair from "../common/BlockTokenPair";
import Button from "../common/Button";

interface HomeRepayCreditFormOnRampStep0Props {
  data: HomeRepayCreditFormOnRampValues;
  next: (values: HomeRepayCreditFormOnRampValues) => void;
  handleBack: () => void;
  onCloseModal: () => void;
}

const HomeRepayCreditFormOnRampStep0Schema = Yup.object().shape({
  amount: Yup.number().required('Please enter an amount to proceed.'),
  fiatCurrency: Yup.string().required("Please select currency to proceed."),
  tokens: Yup.array().of(Yup.string()).notRequired()
})

function HomeRepayCreditFormOnRampStep0(
  {
    data,
    next,
    handleBack,
    onCloseModal,
  }:HomeRepayCreditFormOnRampStep0Props) {

  const USERCREDITAMOUNT = 242541;
  const FIATCURRENCY = data.fiatCurrency.toUpperCase();
  const TOKENCURRENCY = data.tokens[0];

  return (
    <Formik
      initialValues={data}
      validationSchema={HomeRepayCreditFormOnRampStep0Schema}
      validateOnMount={true}
      validateOnChange={true}
      onSubmit={(values) => {
        next(values)
      }}
    >
      {({ isSubmitting, values, isValid, errors, touched }) => (

        <Form
          className="c-form"
          noValidate
        >

          <ModalFormWrapper
            headerTitle="Repay through On-Ramp"
            onClick={handleBack}
            onCloseModal={onCloseModal}
          >
            <FormFieldset
              extraClassName="amount"
              legendText="Select amount"
            >

              <FormInputNumber
                extraClassName="boxed"
                formGroupStyle="block-line"
                name="amount"
                labelText="Amount"
                placeholder={`0.00 ${values.fiatCurrency? FIATCURRENCY : ""}`}
                inputGroupText="$ "
                inputGroupPlacement="left"
                suffix={` ${FIATCURRENCY}`}
                hideError
                decimalScale={2}
                fixedDecimalScale
                formGroupExtraContent={
                  <FormInputSelect
                    name="fiatCurrency"
                    labelText="Select currency"
                    labelIsHidden
                    options={DummyCurrencyOptions}
                    hideError
                    isDisabled
                  />
                }
              />

              <FormInputNumber
                formGroupStyle="block-line"
                name="credit_balance"
                labelText="Credit balance receives"
                value={Number(values.amount) * 0.995}
                inputGroupText="$ "
                inputGroupPlacement="left"
                suffix={` ${TOKENCURRENCY}`}
                disabled
                readOnly
                decimalScale={2}
                fixedDecimalScale
                formGroupExtraContent={
                  <BlockTokenPair
                    tokens={values.tokens}
                    tokenName={TOKENCURRENCY}
                  />
                }
              />

              {errors.amount && touched.amount && (
                <FormGroupErrorText text={errors.amount}/>
              )}

              {errors.fiatCurrency && touched.fiatCurrency && (
                <FormGroupErrorText text={errors.fiatCurrency}/>
              )}

            </FormFieldset>

            <FormFieldset legendText="Details">

              <ul className="c-form-fieldset-detail-list">

                <li className="exchange-rate">
                  <em>Exchange rate</em>
                  <span>
                    <i><IcnSwitchHorizontal/></i>
                    <span>1 {FIATCURRENCY} = 1 {TOKENCURRENCY} <em>($1.00)</em></span>
                  </span>
                </li>

                <li>
                  <em>
                    Estimated fees
                    <Tippy
                      placement="auto"
                      content="Lorem ipsum dolor sit amet, consectetur adipisicing elit. Autem expedita facilis incidunt inventore maiores modi nemo nisi quaerat reprehenderit sint. Harum impedit in incidunt mollitia quod rem unde? Consequatur, est!"
                      className="c-tooltip"
                    >
                      <i className="c-tooltip-icon"><IcnInfoFilled/></i>
                    </Tippy>
                  </em>

                  <BlockNumber
                    value={Number(values.amount) * 0.005}
                    prefix="$"
                    suffix=" USDC"
                    decimalScale={2}
                    fixedDecimalScale
                  />
                </li>

                <li className="is-bordered">
                  <em>Current credit balance</em>
                  <BlockNumber
                    value={USERCREDITAMOUNT}
                    prefix="$"
                    suffix={` ${TOKENCURRENCY}`}
                    decimalScale={2}
                    fixedDecimalScale
                  />
                </li>

                <li>
                  <em>Repay amount</em>
                  <BlockNumber
                    allowNegative={false}
                    value={values.amount|| 0}
                    prefix="-$"
                    suffix={` ${TOKENCURRENCY}`}
                    decimalScale={2}
                    fixedDecimalScale
                  />
                </li>

                <li className="is-bordered dashed">
                  <em>New credit balance</em>
                  <BlockNumber
                    value={values.amount ? USERCREDITAMOUNT - Number(values.amount) : USERCREDITAMOUNT}
                    prefix="$"
                    suffix={` ${TOKENCURRENCY}`}
                    decimalScale={2}
                    fixedDecimalScale
                    renderText={(value) => <span className={clsx(values.amount && "c-text-success")}>{values.amount && (<i><IcnArrowDown/></i>)} {value}</span>}
                  />
                </li>

              </ul>

            </FormFieldset>

            <div className="c-modal-footer">
              <Button
                extraClassName="has-space-between"
                type="submit"
                text="Complete transaction"
                btnSize="lg"
                icon={<IcnArrowRightUp/>}
                disabled={isSubmitting || !isValid}
              />
            </div>

          </ModalFormWrapper>

        </Form>
      )}

    </Formik>
  );
}

export default HomeRepayCreditFormOnRampStep0;